import { useCallback } from 'react';
import { message } from 'antd';
import { t } from '../../../../i18n';
import {
    WaypointTypeEnum,
    TmsStatusEnum,
} from '../../../../enums/TmsEnum';
import { isExternalStationRoute } from '../utils/externalStation';
import { CreateTmsRouteWithWaypointsInput, UpdateTmsRouteWithWaypointsInput, RouteWaypointInput } from '../graphql/mutations/routeMutations';
import { GetHandoverCarriersByWarehouseIdResponse } from '../graphql/queries/getHandoverCarriers';
import { ApiResult } from '../../../../graphql/types/ApiResult';
import { showError } from '../../../../utils/exceptionUtils';
import { parseResultCode } from '../../../../utils/resultCode';
import { DrawerMode, type StationRow, type WarehouseOption, type RouteFormFilters } from '../types/routeFormTypes';

// 从 sortingCode 中提取展示名称
// 如果格式为 "LA-LADC"，提取第二段 "LADC"；否则原样返回
const getStationDisplayName = (sortingCode: string): string => {
    if (!sortingCode) return '';
    const parts = sortingCode.split('-');
    if (parts.length >= 2 && parts[1]) {
        return parts[1];
    }
    return sortingCode;
};

// 生成路线名称，格式：origin-waypoint1-waypoint2-waypoint3-destination
const generateRouteName = (tableData: StationRow[]): string => {
    const origin = tableData.find(row => row.type === WaypointTypeEnum.Origin);
    const destination = tableData.find(row => row.type === WaypointTypeEnum.Destination);
    const waypoints = tableData.filter(row => row.type === WaypointTypeEnum.Waypoint).sort((a, b) => {
        const aIndex = tableData.findIndex(row => row.key === a.key);
        const bIndex = tableData.findIndex(row => row.key === b.key);
        return aIndex - bIndex;
    });

    const nameParts: string[] = [];
    if (origin) nameParts.push(getStationDisplayName(origin.station));
    waypoints.forEach(waypoint => nameParts.push(getStationDisplayName(waypoint.station)));
    if (destination) nameParts.push(getStationDisplayName(destination.station));

    return nameParts.join('-');
};

interface UseRouteFormSubmitParams {
    filters: RouteFormFilters;
    tableData: StationRow[];
    routeCode: string | undefined;
    mode: DrawerMode;
    initialValues: any;
    showWaypointSection: boolean;
    currentWarehouse: any;
    warehouseOptions: WarehouseOption[];
    handoverCarriersData: GetHandoverCarriersByWarehouseIdResponse | undefined;
    totalHours: string;
    totalMiles: string;
    initialCarrierIdRef: React.MutableRefObject<string | undefined>;
    initialTableDataRef: React.MutableRefObject<StationRow[]>;
    resolveExtWaypointIdFromRow: (row: StationRow) => number | undefined;
    validateForm: () => boolean;
    validateWaypointDuplicates: (
        waypoints: RouteWaypointInput[],
        startWarehouseId: number,
        endWarehouseId: number,
        isExternalStationRoute: boolean,
        destExtWaypointId?: number | null
    ) => boolean;
    createRouteWithWaypoints: (options: any) => Promise<any>;
    updateRouteWithWaypoints: (options: any) => Promise<any>;
    onSubmit: (values: any) => void;
}

export const useRouteFormSubmit = ({
    filters,
    tableData,
    routeCode,
    mode,
    initialValues,
    showWaypointSection,
    currentWarehouse,
    warehouseOptions,
    handoverCarriersData,
    totalHours,
    totalMiles,
    initialCarrierIdRef,
    initialTableDataRef,
    resolveExtWaypointIdFromRow,
    validateForm,
    validateWaypointDuplicates,
    createRouteWithWaypoints,
    updateRouteWithWaypoints,
    onSubmit,
}: UseRouteFormSubmitParams) => {

    // 检测是否有修改（仅在编辑模式下）
    const checkForChanges = useCallback((): boolean => {
        if (mode !== DrawerMode.Edit) {
            return false;
        }

        // 1.检测承运商是否修改
        const carrierChanged = filters.carrierId !== initialCarrierIdRef.current;

        // 2.检测途径站点、travelTime、dwellTime、distance是否修改
        const initialData = initialTableDataRef.current;
        const currentData = tableData;

        // 3.检测起点的停留时间（dwellTime）是否修改（起点和终点的station都不能修改）
        const initialOrigin = initialData.find(row => row.type === WaypointTypeEnum.Origin);
        const currentOrigin = currentData.find(row => row.type === WaypointTypeEnum.Origin);
        if (initialOrigin && currentOrigin && initialOrigin.dwellTime !== currentOrigin.dwellTime) {
            return true;
        }

        // 4.检测终点的travelTime、dwellTime、distance是否修改（终点的station不能修改）
        const initialDestination = initialData.find(row => row.type === WaypointTypeEnum.Destination);
        const currentDestination = currentData.find(row => row.type === WaypointTypeEnum.Destination);
        if (initialDestination && currentDestination) {
            if (
                initialDestination.travelTime !== currentDestination.travelTime ||
                initialDestination.dwellTime !== currentDestination.dwellTime ||
                initialDestination.distance !== currentDestination.distance
            ) {
                return true;
            }
        }

        // 5.比较途径站点（只检测途经点，因为起点和终点的station都不能修改）
        const initialWaypoints = initialData.filter(row => row.type === WaypointTypeEnum.Waypoint);
        const currentWaypoints = currentData.filter(row => row.type === WaypointTypeEnum.Waypoint);

        // 6.检查途经点数量是否变化
        if (initialWaypoints.length !== currentWaypoints.length) {
            return true;
        }

        // 7.检查每个途经点的station、travelTime、dwellTime、distance是否变化（Temu 另比 extWaypointId）
        const isExternalStationForCompare = isExternalStationRoute(filters.businessType, filters.routeType);
        for (let i = 0; i < initialWaypoints.length; i++) {
            const initial = initialWaypoints[i];
            const current = currentWaypoints[i];

            if (
                initial.station !== current.station ||
                initial.travelTime !== current.travelTime ||
                initial.dwellTime !== current.dwellTime ||
                initial.distance !== current.distance ||
                (isExternalStationForCompare &&
                    (initial.extWaypointId ?? null) !== (current.extWaypointId ?? null))
            ) {
                return true;
            }
        }

        return carrierChanged;
    }, [mode, filters.carrierId, filters.businessType, filters.routeType, tableData]);

    // 公共：计算起点仓库ID
    const getStartWarehouseId = (): number | null => {
        return currentWarehouse?.id ? parseInt(currentWarehouse.id.toString()) : null;
    };

    // 公共：根据站点名称解析仓库ID
    const resolveWarehouseIdFromStation = (station: string) => {
        const trimmed = station.trim();
        if (!trimmed) return null;
        const byCode = warehouseOptions.find(w => w.sortingCode === trimmed);
        if (byCode) return parseInt(byCode.id, 10);
        const byName = warehouseOptions.find(w => w.name?.trim() === trimmed);
        return byName ? parseInt(byName.id, 10) : null;
    };

    // 公共：是否为外部站点路线
    const isExternalStation = isExternalStationRoute(filters.businessType, filters.routeType);

    // 公共：构建途经点列表
    const buildWaypointList = (
        startId: number,
        endId: number,
        waypoints: StationRow[],
        destination: StationRow
    ): RouteWaypointInput[] | null => {
        const waypointList: RouteWaypointInput[] = [];
        const originRow = tableData.find(row => row.type === WaypointTypeEnum.Origin);

        waypointList.push({
            waypointId: startId,
            waypointOrder: 0,
            waypointType: WaypointTypeEnum.Origin,
            waypointStatus: TmsStatusEnum.Active,
            travelTime: null,
            dwellTime: originRow?.dwellTime ?? null,
            distance: null,
        });

        for (let i = 0; i < waypoints.length; i++) {
            const waypoint = waypoints[i];
            const waypointWarehouseId = isExternalStation
                ? -1
                : resolveWarehouseIdFromStation(waypoint.station);
            if (!isExternalStation && !waypointWarehouseId) {
                message.error(t('tms.route.messages.invalidWaypoint', { station: waypoint.station }));
                return null;
            }
            waypointList.push({
                waypointId: waypointWarehouseId as number,
                waypointOrder: i + 1,
                waypointType: WaypointTypeEnum.Waypoint,
                waypointStatus: TmsStatusEnum.Active,
                travelTime: waypoint.travelTime ?? null,
                dwellTime: waypoint.dwellTime ?? null,
                distance: waypoint.distance ?? null,
                ...(isExternalStation
                    ? { extWaypointId: resolveExtWaypointIdFromRow(waypoint) }
                    : {}),
            });
        }

        waypointList.push({
            waypointId: endId,
            waypointOrder: waypoints.length + 1,
            waypointType: WaypointTypeEnum.Destination,
            waypointStatus: TmsStatusEnum.Active,
            travelTime: destination.travelTime ?? null,
            dwellTime: destination.dwellTime ?? null,
            distance: destination.distance ?? null,
            ...(isExternalStation
                ? { extWaypointId: resolveExtWaypointIdFromRow(destination) }
                : {}),
        });

        return waypointList;
    };

    // 公共：执行 create 或 edit mutation
    const executeMutation = async (input: CreateTmsRouteWithWaypointsInput | UpdateTmsRouteWithWaypointsInput) => {
        const isEdit = mode === DrawerMode.Edit && initialValues?.id;
        const { data } = isEdit
            ? await updateRouteWithWaypoints({ variables: { id: initialValues.id, input } })
            : await createRouteWithWaypoints({ variables: { input } });

        const result: ApiResult = isEdit ? data!.updateTmsRouteWithWaypoints : data!.createTmsRouteWithWaypoints;

        if (!result.success) {
            message.error(parseResultCode(result?.code ?? '', result?.message) || t('tms.route.messages.failedToSaveRoute'));
            return;
        }

        message.success(mode === DrawerMode.Edit
            ? t('tms.route.messages.routeUpdatedSuccessfully')
            : t('tms.route.messages.routeCreatedSuccessfully')
        );
        onSubmit({ filters, tableData });
    };

    /** 带途经点的提交（含校验、构建 waypoints、调用 mutation） */
    const submitWithWaypoints = async () => {
        const destination = tableData.find(row => row.type === WaypointTypeEnum.Destination);
        const waypoints = tableData.filter(row => row.type === WaypointTypeEnum.Waypoint);

        const startWarehouseId = getStartWarehouseId();
        const endWarehouseId = isExternalStation
            ? -1
            : resolveWarehouseIdFromStation(destination!.station);

        if (!startWarehouseId || (!isExternalStation && !endWarehouseId)) {
            message.error(t('tms.route.messages.invalidWarehouseInformation'));
            return;
        }
        const resolvedStartWarehouseId = startWarehouseId as number;
        const resolvedEndWarehouseId = endWarehouseId as number;

        // 构建途经点列表
        const waypointList = buildWaypointList(resolvedStartWarehouseId, resolvedEndWarehouseId, waypoints, destination!);
        if (!waypointList) return;

        // 获取终点的 extWaypointId 用于重复校验
        const destinationWaypoint = waypointList[waypointList.length - 1];

        // 重复途经点校验
        if (!validateWaypointDuplicates(waypointList, resolvedStartWarehouseId, resolvedEndWarehouseId, isExternalStation, destinationWaypoint.extWaypointId)) {
            return;
        }

        // 生成路线名称：origin-waypoint1-waypoint2-waypoint3-destination
        const routeName = generateRouteName(tableData);

        const input = {
            routeName,
            routeCode: routeCode?.trim() || undefined,
            routeType: filters.routeType!,
            businessType: filters.businessType!,
            carrierId: parseInt(filters.carrierId!),
            status: filters.status!,
            startWarehouseId: resolvedStartWarehouseId,
            endWarehouseId: resolvedEndWarehouseId,
            plannedTotalHours: parseFloat(totalHours),
            plannedTotalMiles: parseFloat(totalMiles),
            waypoints: waypointList.map(({ extWaypointId, ...rest }) =>
                rest.waypointType === WaypointTypeEnum.Origin ? rest : { ...rest, extWaypointId }
            )
        };

        await executeMutation(input);
    };

    /** 不带途经点的提交（交接类型 + 非 TemuGA） */
    const submitWithoutWaypoints = async () => {
        const startWarehouseId = getStartWarehouseId();
        if (!startWarehouseId) {
            message.error(t('tms.route.messages.invalidWarehouseInformation'));
            return;
        }

        // 生成路线名称：始发站(交接方shortCarrierName)
        const originDisplayName = getStationDisplayName(currentWarehouse?.sortingCode || '');
        const handoverCarrier = handoverCarriersData?.getHandoverCarriersByWarehouseId?.find(
            hc => hc.id === filters.carrierId
        );
        const routeName = `${originDisplayName}(${handoverCarrier?.shortCarrierName ?? ''})`;

        // 只有一条 Origin 途经点
        const waypoints: RouteWaypointInput[] = [{
            waypointId: startWarehouseId,
            waypointOrder: 0,
            waypointType: WaypointTypeEnum.Origin,
            waypointStatus: TmsStatusEnum.Active,
            travelTime: null,
            dwellTime: null,
            distance: null,
        }];

        const input = {
            routeName,
            routeCode: routeCode?.trim() || undefined,
            routeType: filters.routeType!,
            businessType: filters.businessType!,
            carrierId: parseInt(filters.carrierId!),
            status: filters.status!,
            startWarehouseId,
            endWarehouseId: null,
            plannedTotalHours: 0,
            plannedTotalMiles: 0,
            waypoints,
        };

        await executeMutation(input);
    };

    const performSubmit = async () => {
        try {
            if (!validateForm()) {
                return;
            }

            if (showWaypointSection) {
                await submitWithWaypoints();
            } else {
                await submitWithoutWaypoints();
            }
        } catch (error: any) {
            console.error('Failed to save route:', error);
            const hasMessageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey;
            hasMessageKey ? showError(error) : message.error(error?.message || t('tms.route.messages.failedToSaveRoute'));
        }
    };

    return { performSubmit, checkForChanges };
};
